import { AdminIntroductions } from '@platform/client';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { AdminIntroduction } from '@platform/client/schemas';
import { useMemo, useState, useContext } from 'react';
import { AdminTable, AdminTableContext, DateCellFromString } from '../../AdminTable';
import { AdminListPageWrapper } from '../../AdminListPage';
import { CreateIntroductionModal } from './CreateIntroductionModal';
import { ProjectLink } from '../projects/ProjectsAdmin';
import { OrganizationLink } from '../organizations/OrganizationsAdmin';
import { ProfessionalLink } from '../professionals/ProfessionalsAdmin';
import { Link } from 'wouter';
import { ADMIN_ROUTES, ROUTES } from '../../../routes/consts';
import { ImportIntroductionsModal } from './ImportIntroductionsModal';

const columnHelper = createColumnHelper<AdminIntroduction>();

export const IntroductionsAdmin = () => {
  return (
    <AdminListPageWrapper>
      <IntroductionsAdminContent />
    </AdminListPageWrapper>
  );
};

const IntroductionsAdminContent = () => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [importIntrosModalOpen, setImportIntrosModalOpen] = useState(false);

  const { data, isError, isLoading, isSuccess } = AdminIntroductions.useGetIntroductions(
    { ...adminQueryOptionsV2 },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const adminIntroductions = data?.introductions || [];
  const adminIntroductionsCount = data?.totalCount || 0;

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('introductionId', {
          cell: (introductionId) => (
            <IntroductionLink
              introductionId={introductionId.getValue()}
              introductionName={introductionId.getValue().toString()}
            />
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>ID</span>,
          maxSize: 10,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
          enableSorting: true,
        }),
        columnHelper.accessor('introductionInitiationType', {
          cell: (sentByUserType) => <div>{sentByUserType.getValue()}</div>,
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Sent By</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('professionalName', {
          id: 'professionalName',
          cell: ({ row }) => (
            <ProfessionalLink
              userId={row.original.professionalId}
              name={row.original.professionalName}
            />
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Pro Name</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('cohort', {
          cell: (cohort) => cohort.getValue(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Intro Cohort</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('representativeName', {
          cell: (repName) => repName.getValue(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Rep Name</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('organizationName', {
          cell: ({ row }) => (
            <OrganizationLink
              organizationId={row.original.organizationId}
              organizationName={row.original.organizationName}
            />
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Org Name</span>,
          size: 125,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('projectName', {
          cell: ({ row }) => (
            <ProjectLink
              projectId={row.original.projectId}
              projectName={row.original.projectName}
            />
          ),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Project Name</span>,
          size: 150,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('myProjectsStatus', {
          cell: (info) => info.getValue(),
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Match Status</span>,
          size: 25,
          enableColumnFilter: true,
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('myProjectsStatusDate', {
          cell: (cell) => <DateCellFromString value={cell.getValue()} />,
          header: () => (
            <span className='overflow-ellipsis whitespace-nowrap'>Match Status Date</span>
          ),
          size: 25,
          enableColumnFilter: false,
        }),
      ] as ColumnDef<AdminIntroduction>[],
    [],
  );

  return (
    <>
      <AdminTable
        columns={columns}
        data={adminIntroductions}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        filteredCount={adminIntroductionsCount}
        enableFiltering={true}
        tableActions={[
          {
            text: 'Create introduction',
            callback: () => setCreateModalOpen(true),
          },
          {
            text: 'Import introductions',
            callback: () => setImportIntrosModalOpen(true),
          },
        ]}
      />
      {createModalOpen && <CreateIntroductionModal onClose={() => setCreateModalOpen(false)} />}
      <ImportIntroductionsModal
        isOpen={importIntrosModalOpen}
        onClose={() => setImportIntrosModalOpen(false)}
      />
    </>
  );
};

export const IntroductionLink = ({
  introductionId,
  introductionName,
}: {
  introductionId: number;
  introductionName: string;
}) => {
  if (introductionId && introductionName) {
    return (
      <Link
        to={`~/platform${ROUTES.ADMIN}${ADMIN_ROUTES.INTRODUCTIONS}/${introductionId}`}
        className='text-brand-500 hover:underline'
      >
        {introductionName}
      </Link>
    );
  }
};
